import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { alpha, styled } from "@mui/material/styles";

import { useTranslate } from "@/hooks";
import { brand, colors } from "@/theme";

/** The two ways a game can be played: one bet at a time, or a plan. */
export const BET_MODES = ["manual", "auto"] as const;

export type BetMode = (typeof BET_MODES)[number];

/** Where each mode's label lives in the message catalogue. */
const MODE_KEYS: Record<BetMode, string> = {
  manual: "game.shared.mode.manual",
  auto: "game.shared.mode.auto",
};

/**
 * The track the two modes sit in.
 *
 * A filled pill rather than MUI's tab rail, because this is not navigation -
 * nothing is behind it to go back to. It is a switch between two ways of
 * placing the same bet, and a segmented control is what that looks like.
 */
const Track = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  borderRadius: 999,
  border: "1px solid",
  borderColor: theme.palette.divider,

  // `action.hover` is a 20% grey: on the dark panel that is the step up from
  // it this needs, and on the white one it is nothing at all - which leaves the
  // two modes as bare words rather than a control. So the light scheme gets a
  // track whose ends you can see.
  backgroundColor: theme.vars.palette.game.track,
  ...theme.applyStyles("dark", { backgroundColor: theme.palette.action.hover }),
}));

/** One mode. */
const Segment = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  minHeight: 34,
  padding: theme.spacing(0.5, 1),
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0.3,
  // The selected mode is filled in. The old rail marked it with a 2px
  // underline, which on a 320px column reads as a border on the panel rather
  // than as the state of a control.
  //
  // The fill is the brand blue in both schemes, but the word on it cannot be:
  // `#2E90FF` on a white panel is a 2.6:1 figure. So the light scheme takes the
  // deep end of the brand ramp - the same color, enough of it to read.
  backgroundColor: selected ? alpha(colors.prime, 0.18) : "transparent",
  color: selected ? brand[700] : theme.palette.text.secondary,
  boxShadow: selected ? `inset 0 0 0 1px ${alpha(colors.prime, 0.55)}` : "none",
  ...theme.applyStyles("dark", {
    backgroundColor: selected ? alpha(colors.prime, 0.16) : "transparent",
    color: selected ? colors.prime : theme.palette.text.secondary,
    boxShadow: selected ? `inset 0 0 0 1px ${alpha(colors.prime, 0.45)}` : "none",
  }),
  transition: theme.transitions.create(["background-color", "color", "box-shadow"], {
    duration: theme.transitions.duration.shortest,
  }),

  "&:hover:not(:disabled)": {
    backgroundColor: selected ? alpha(colors.prime, 0.26) : theme.palette.action.selected,
    color: selected ? brand[800] : theme.palette.text.primary,
    ...theme.applyStyles("dark", {
      backgroundColor: selected ? alpha(colors.prime, 0.22) : theme.palette.action.selected,
      color: selected ? colors.prime : theme.palette.text.primary,
    }),
  },

  "&:focus-visible": {
    outline: `2px solid ${alpha(colors.prime, 0.8)}`,
    outlineOffset: 2,
  },

  "&:disabled": {
    cursor: "default",
    opacity: 0.45,
  },
}));

/**
 * The light on the auto segment while a session is placing bets.
 *
 * Until now the only sign a session was still running was that the manual tab
 * had gone grey - a disabled control standing in for a status, which reads as
 * a bug rather than as "bets are going in".
 */
const RunningDot = styled("span")(({ theme }) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  flexShrink: 0,
  // The outcome token rather than the raw mint: a `#00E676` dot on a white
  // panel is a pale smudge, where the light scheme's deeper green reads as a
  // light that is on.
  backgroundColor: theme.vars.palette.game.win,
  boxShadow: `0 0 0 3px rgba(${theme.vars.palette.game.winChannel} / 0.2)`,
  animation: "bet-mode-pulse 1.4s ease-in-out infinite",
  "@keyframes bet-mode-pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.3 },
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

export interface BetModeSwitchProps {
  value: BetMode;
  onChange: (mode: BetMode) => void;
  /**
   * True while an auto session is in flight.
   *
   * The mode it is running in is then the only one on offer: leaving it
   * mid-session would hide the plan that is still spending the player's money.
   */
  running?: boolean;
}

/**
 * Manual or auto, as one switch at the top of the controls.
 *
 * Both modes place the same bet at the same stake - auto just keeps placing it
 * - so the switch says which hand is on the button and nothing else. Every
 * game rendered its own copy of a `Tabs`/`Tab` pair with the same four sx
 * overrides on it; this is that, once, with the running session visible.
 */
const BetModeSwitch = ({ value, onChange, running = false }: BetModeSwitchProps) => {
  const t = useTranslate();

  return (
    <Tooltip arrow title={running ? t("game.shared.mode.locked") : ""}>
      <Track role="tablist" aria-label={t("game.shared.mode.aria-label")}>
        {BET_MODES.map((mode) => (
          <Segment
            key={mode}
            type="button"
            role="tab"
            selected={value === mode}
            aria-selected={value === mode}
            disabled={running && mode !== value}
            onClick={() => onChange(mode)}
          >
            {t(MODE_KEYS[mode])}
            {mode === "auto" && running ? (
              <RunningDot aria-label={t("game.shared.mode.running")} />
            ) : null}
          </Segment>
        ))}
      </Track>
    </Tooltip>
  );
};

export default BetModeSwitch;
